// Utility functions related to payments

/**
 * Format currency value
 * @param {number} value - The value to format
 * @param {string} [currency='€'] - Currency symbol
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, currency = '€') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };
  
  /**
   * Calculate payment status
   * @param {string} bookingDate - Date of the payment
   * @param {number|null} cashed - Amount cashed
   * @returns {string} Payment status (overdue, soon, upcoming, paid)
   */
  export const calculatePaymentStatus = (bookingDate, cashed) => {
    const today = moment();
    const paymentMoment = moment(bookingDate);
    const diffInDays = paymentMoment.diff(today, 'days');
  
    if (cashed !== null && cashed > 0) return 'paid';
    if (diffInDays < 0) return 'overdue';
    if (diffInDays <= 7) return 'soon';
    return 'upcoming';
  };
  
  /**
   * Get status color based on payment status
   * @param {string} status - Payment status
   * @returns {string} Tailwind CSS color class
   */
  export const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-green-600';
      case 'overdue': return 'text-red-600';
      case 'soon': return 'text-yellow-600';
      case 'upcoming': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };
  
  /**
   * Validate payment
   * @param {Object} payment - Payment object to validate
   * @returns {Object} Validation result
   */
  export const validatePayment = (payment) => {
    const errors = {};
  
    if (!payment.type) {
      errors.type = 'Payment type is required';
    }
  
    if (!payment.booking_date) {
      errors.booking_date = 'Booking date is required';
    }
  
    if (payment.unitAmounts && payment.unitAmounts.length > 0) {
      payment.unitAmounts.forEach((unit, index) => {
        if (!unit.amount || parseFloat(unit.amount) <= 0) {
          errors[`unitAmounts[${index}]`] = 'Invalid amount';
        }
      });
    }
  
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  };
  
  /**
   * Normalize payment data
   * @param {Object} payment - Raw payment object
   * @returns {Object} Normalized payment object
   */
  export const normalizePayment = (payment) => {
    return {
      ...payment,
      amount: parseFloat(payment.amount || 0),
      booking_date: moment(payment.booking_date).format('YYYY-MM-DD'),
      unitAmounts: payment.unitAmounts?.map(unit => ({
        ...unit,
        amount: parseFloat(unit.amount || 0)
      })) || []
    };
  };